import { useEffect, useMemo, useState } from 'react';
import { Note, Panel, ReportView, Spinner, type ReportDocument, type ReportTable } from '@wyocspay/ui';
import { api, saveBlob, type ReportQuery, type ReportSection } from './api.js';
import { useLocale } from './locale.js';

/**
 * Reports, for the merchant's own bookkeeping and for their accountant.
 *
 * The screen is deliberately in two halves. Above, the smallest set of choices
 * that can produce a document: a period and what to include. Below, the
 * document itself, drawn by the same component and from the same structure the
 * server uses to produce the file - so what is checked here is what leaves
 * here.
 *
 * The export controls live with the document rather than with the form,
 * because they act on a document that exists. A "Download CSV" button beside a
 * date picker invites somebody to export a report they have not looked at.
 */

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------

/** `2026-07-29`, in UTC, so a preset means the same thing to the server. */
function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function startOfMonth(offset: number): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
}

function endOfMonth(offset: number): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0));
}

function startOfQuarter(offset: number): Date {
  const now = new Date();
  const quarter = Math.floor(now.getUTCMonth() / 3) + offset;
  return new Date(Date.UTC(now.getUTCFullYear(), quarter * 3, 1));
}

function endOfQuarter(offset: number): Date {
  const now = new Date();
  const quarter = Math.floor(now.getUTCMonth() / 3) + offset;
  return new Date(Date.UTC(now.getUTCFullYear(), quarter * 3 + 3, 0));
}

interface Preset {
  key: string;
  label: string;
  range: () => { from: string; to: string };
}

/**
 * The periods an accountant actually asks for.
 *
 * A quarter and a month, this one and the last, plus the year. Anything else is
 * two dates, which is why the custom fields are always visible rather than
 * hidden behind a "custom" mode - a preset fills them in and they can be edited
 * from there, so there is never a state where the dates on screen are not the
 * dates that will be used.
 */
const PRESETS: Preset[] = [
  {
    key: 'this-month',
    label: 'This month',
    range: () => ({ from: isoDay(startOfMonth(0)), to: isoDay(new Date()) }),
  },
  {
    key: 'last-month',
    label: 'Last month',
    range: () => ({ from: isoDay(startOfMonth(-1)), to: isoDay(endOfMonth(-1)) }),
  },
  {
    key: 'this-quarter',
    label: 'This quarter',
    range: () => ({ from: isoDay(startOfQuarter(0)), to: isoDay(new Date()) }),
  },
  {
    key: 'last-quarter',
    label: 'Last quarter',
    range: () => ({ from: isoDay(startOfQuarter(-1)), to: isoDay(endOfQuarter(-1)) }),
  },
  {
    key: 'this-year',
    label: 'This year',
    range: () => ({
      from: isoDay(new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1))),
      to: isoDay(new Date()),
    }),
  },
];

const SECTIONS: Array<{ key: ReportSection; label: string; hint: string }> = [
  { key: 'all', label: 'Everything', hint: 'Transactions, fees, refunds, disputes and payouts' },
  { key: 'transactions', label: 'Transactions', hint: 'Every payment, with gross, refunded and net' },
  { key: 'fees', label: 'Fees paid', hint: 'What the platform and Stripe each took' },
  { key: 'refunds', label: 'Refunds and disputes', hint: 'What each one cost' },
  { key: 'payouts', label: 'Payouts', hint: 'What reached your bank, and what it settled' },
];

// ---------------------------------------------------------------------------

/**
 * Open the printable document and put the print dialogue in front of it.
 *
 * The window is opened before the request goes out, because a browser only
 * allows a new window from a gesture the user actually made - open it after an
 * await and the popup blocker takes it. If it is blocked anyway, the caller
 * falls back to saving the document as a file, which prints just as well.
 */
async function printDocument(
  query: ReportQuery,
  fetchHtml: () => Promise<string>,
  onBlocked: (html: string) => void,
): Promise<void> {
  const target = window.open('', '_blank', 'noopener,noreferrer');
  const html = await fetchHtml();

  if (!target || target.closed) {
    onBlocked(html);
    return;
  }

  target.document.open();
  target.document.write(html);
  target.document.close();
  target.document.title = `${query.section} ${query.from} ${query.to}`;

  /**
   * Printing waits for the document to be laid out. Without the delay the
   * dialogue can open against a page whose embedded fonts have not been applied
   * yet, and the preview shows the fallback face.
   */
  target.setTimeout(() => {
    target.focus();
    target.print();
  }, 350);
}

// ---------------------------------------------------------------------------

export function ReportsView() {
  const locale = useLocale();

  const [range, setRange] = useState(() => PRESETS[1].range());
  const [preset, setPreset] = useState('last-month');
  const [section, setSection] = useState<ReportSection>('all');

  const [document_, setDocument] = useState<ReportDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [maxDays, setMaxDays] = useState(400);

  useEffect(() => {
    let live = true;
    void api
      .reportMeta()
      .then((meta) => {
        if (live) setMaxDays(meta.max_period_days);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const query = useMemo<ReportQuery>(
    () => ({ from: range.from, to: range.to, section }),
    [range.from, range.to, section],
  );

  const days = useMemo(() => {
    const from = Date.parse(`${range.from}T00:00:00Z`);
    const to = Date.parse(`${range.to}T00:00:00Z`);
    if (Number.isNaN(from) || Number.isNaN(to)) return null;
    return Math.round((to - from) / 86_400_000) + 1;
  }, [range.from, range.to]);

  const rangeProblem =
    days === null ? 'Enter two dates.'
    : days <= 0 ? 'The closing date falls before the opening date.'
    : days > maxDays ? `A single report covers at most ${maxDays} days. Split the period.`
    : null;

  async function generate(): Promise<void> {
    if (rangeProblem) return;
    setLoading(true);
    setError(null);
    try {
      setDocument(await api.report(query));
    } catch (err) {
      setDocument(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function withBusy(key: string, work: () => Promise<void>): Promise<void> {
    setBusy(key);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const stem = document_?.slug ?? `wyocspay-${range.from}-to-${range.to}`;

  return (
    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <Note title="These documents are meant to leave the platform">
        Everything below can be printed as a PDF, opened in Excel or saved as CSV. Each figure is
        stated in the currency it was transacted in, and amounts in different currencies are never
        added together - so a total always names the currency it belongs to.
      </Note>

      <Panel title="Choose a period">
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <span className="wc-label" id="period-presets">
              Period
            </span>
            <div
              role="group"
              aria-labelledby="period-presets"
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            >
              {PRESETS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={preset === option.key}
                  className={`wc-btn wc-btn-sm ${preset === option.key ? '' : 'wc-btn-secondary'}`}
                  onClick={() => {
                    setPreset(option.key);
                    setRange(option.range());
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
              gap: 14,
            }}
          >
            <label className="wc-field" style={{ marginBottom: 0 }}>
              <span className="wc-label">From</span>
              <input
                type="date"
                className="wc-input"
                value={range.from}
                max={range.to}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, from: e.target.value }));
                }}
              />
            </label>
            <label className="wc-field" style={{ marginBottom: 0 }}>
              <span className="wc-label">To</span>
              <input
                type="date"
                className="wc-input"
                value={range.to}
                min={range.from}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, to: e.target.value }));
                }}
              />
            </label>
            <label className="wc-field" style={{ marginBottom: 0 }}>
              <span className="wc-label">Include</span>
              <select
                className="wc-input"
                value={section}
                onChange={(e) => setSection(e.target.value as ReportSection)}
              >
                {SECTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span
                style={{ display: 'block', marginTop: 4, fontSize: 12.5, color: 'var(--wc-faint)' }}
              >
                {SECTIONS.find((s) => s.key === section)?.hint}
              </span>
            </label>
          </div>

          {rangeProblem ? (
            <Note tone="warn" title="That period cannot be reported on">
              {rangeProblem}
            </Note>
          ) : null}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="wc-btn"
              disabled={loading || Boolean(rangeProblem)}
              onClick={() => void generate()}
            >
              {loading ? 'Preparing…' : 'Prepare report'}
            </button>
            {days && days > 0 ? (
              <span style={{ color: 'var(--wc-muted)', fontSize: 13 }}>
                {locale.dateLong(`${range.from}T00:00:00Z`)} to{' '}
                {locale.dateLong(`${range.to}T00:00:00Z`)} · {days}{' '}
                {days === 1 ? 'day' : 'days'}
              </span>
            ) : null}
          </div>

          {section === 'fees' || section === 'all' ? (
            <p style={{ margin: 0, color: 'var(--wc-faint)', fontSize: 12.5, maxWidth: '80ch' }}>
              The fee figures are read from Stripe one payment at a time, because the only
              authoritative record of what a charge cost is its balance transaction. A long period
              therefore takes a little while, and the report says how many payments it covered.
            </p>
          ) : null}
        </div>
      </Panel>

      {error ? (
        <Note tone="danger" title="The report could not be produced">
          {error}
        </Note>
      ) : null}

      {loading ? (
        <Panel>
          <Spinner label="Reading your records…" />
        </Panel>
      ) : null}

      {document_ && !loading ? (
        <ReportView
          document={document_}
          actions={
            <>
              {/*
                The PDF is the primary action, and it is a real file now rather
                than "open the document and press print": the server produces a
                finished .pdf in one request, so it is downloaded with the
                session token the same way the workbook is.
              */}
              <button
                type="button"
                className="wc-btn wc-btn-sm"
                disabled={busy !== null}
                onClick={() =>
                  void withBusy('pdf', async () => {
                    const blob = await api.reportFile(query, 'pdf');
                    saveBlob(blob, `${stem}.pdf`);
                  })
                }
              >
                {busy === 'pdf' ? 'Preparing…' : 'Download PDF'}
              </button>
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                disabled={busy !== null}
                onClick={() =>
                  void withBusy('print', async () => {
                    await printDocument(
                      query,
                      () => api.reportHtml(query),
                      (html) => {
                        // The window was blocked. The document is already here,
                        // so it is saved instead - it opens and prints from disk
                        // exactly as it would have in a tab.
                        saveBlob(new Blob([html], { type: 'text/html' }), `${stem}.html`);
                        setError(
                          'Your browser blocked the new window, so the document was saved instead. Open the file and print it from there.',
                        );
                      },
                    );
                  })
                }
              >
                {busy === 'print' ? 'Opening…' : document_.words.printButton}
              </button>
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                disabled={busy !== null}
                onClick={() =>
                  void withBusy('xlsx', async () => {
                    const blob = await api.reportFile(query, 'xlsx');
                    saveBlob(blob, `${stem}.xlsx`);
                  })
                }
              >
                {busy === 'xlsx' ? 'Preparing…' : 'Excel workbook'}
              </button>
            </>
          }
          tableActions={(table: ReportTable) =>
            table.rows.length === 0 ? null : (
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                disabled={busy !== null}
                onClick={() =>
                  void withBusy(`csv-${table.key}`, async () => {
                    const blob = await api.reportFile(query, 'csv', table.key);
                    saveBlob(blob, `${stem}-${table.key}.csv`);
                  })
                }
              >
                {busy === `csv-${table.key}` ? 'Preparing…' : 'CSV'}
              </button>
            )
          }
        />
      ) : null}

      {!document_ && !loading && !error ? (
        <Panel>
          <div
            style={{
              padding: '40px 16px',
              textAlign: 'center',
              color: 'var(--wc-faint)',
              fontSize: 14,
            }}
          >
            Choose a period and prepare a report. Nothing is generated until you ask for it.
          </div>
        </Panel>
      ) : null}
    </div>
  );
}
